import type { GeneratedQuestion } from './generators/math/types';

export type ExamMode = 'practice' | 'formal';

export interface ExamSubmitResult {
  correct: boolean;
  done: boolean;
}

export interface ExamFinalizeResult {
  correctCount: number;
  totalQuestions: number;
  totalXP: number;
  perQuestion: { question: GeneratedQuestion; answer: number | string | null; correct: boolean }[];
}

export class ExamController {
  private questions: GeneratedQuestion[];
  private answers: (number | string | null)[];
  private idx = 0;
  startedAtMs: number;
  done = false;

  constructor(
    questions: GeneratedQuestion[],
    public timeLimitSec: number,
    public mode: ExamMode = 'practice',
    nowFn: () => number = Date.now,
  ) {
    this.questions = questions;
    this.answers = new Array(questions.length).fill(null);
    this.startedAtMs = nowFn();
    this._nowFn = nowFn;
  }
  private _nowFn: () => number;

  currentQuestion(): GeneratedQuestion {
    if (this.done) throw new Error('Exam already done');
    return this.questions[this.idx];
  }

  position(): { current: number; total: number } {
    return { current: this.idx + 1, total: this.questions.length };
  }

  remainingSec(): number {
    return Math.max(0, this.timeLimitSec - (this._nowFn() - this.startedAtMs) / 1000);
  }

  isTimedOut(): boolean {
    return this._nowFn() - this.startedAtMs > this.timeLimitSec * 1000;
  }

  /** Submit an answer for the current question. */
  submit(answer: number | string): ExamSubmitResult {
    if (this.done) throw new Error('Exam already done');
    this.answers[this.idx] = answer;
    const correct = this.judgeQuestion(this.questions[this.idx], answer);
    this.advance();
    return { correct, done: this.done };
  }

  /** Skip the current question. Practice mode only (caller enforces); records as null. */
  skip(): ExamSubmitResult {
    if (this.done) throw new Error('Exam already done');
    this.answers[this.idx] = null;
    this.advance();
    return { correct: false, done: this.done };
  }

  private advance(): void {
    if (this.idx >= this.questions.length - 1) {
      this.done = true;
    } else {
      this.idx++;
    }
  }

  finalize(): ExamFinalizeResult {
    this.done = true;
    const perQuestion = this.questions.map((q, i) => ({
      question: q,
      answer: this.answers[i],
      correct: this.answers[i] !== null && this.judgeQuestion(q, this.answers[i]!),
    }));
    const correctCount = perQuestion.filter(p => p.correct).length;
    // Exam phase mult = 1.2; flat difficulty 3 base (20); combo doesn't apply
    const totalXP = correctCount * 20 * 1.2;
    return { correctCount, totalQuestions: this.questions.length, totalXP, perQuestion };
  }

  private judgeQuestion(q: GeneratedQuestion, answer: number | string): boolean {
    if (q.kind === 'mcq') {
      return typeof answer === 'number' && answer === q.correctAnswer;
    }
    if (q.kind === 'fill') {
      const accepted = q.acceptedAnswers ?? [q.correctAnswer];
      const trimmed = typeof answer === 'string' ? answer.trim() : '';
      return accepted.includes(trimmed);
    }
    // worked-example: not gradable.
    return false;
  }
}
